



















































import Vue, { PropType } from 'vue';
import CSPService, {
    CandidateSchool,
    CandidateStudyProgram,
    StudyField,
    StudyLevel,
    StudyProgram,
} from '~/services/CSPService';
import { CandidateJobBuilder } from '~/utils/candidate';
import InfoBox from '../components/InfoBox.vue';
import StudyProgramDetails from '../components/StudyProgramDetails.vue';
import StudyProgramForm, { CSPForm } from '../components/StudyProgramForm.vue';
import cloneDeep from 'lodash/cloneDeep';

export default Vue.extend({
    name: 'StudyProgramsForm',
    components: {
        InfoBox,
        StudyProgramDetails,
        StudyProgramForm,
    },
    props: {
        builder: {
            type: Object as PropType<CandidateJobBuilder>,
            required: true,
        },
        existingPrograms: {
            type: Array as PropType<CandidateStudyProgram[]>,
            required: true,
        },
    },
    data() {
        return {
            cspService: new CSPService(),

            programs: this.builder.getCSPs(),
            editingProgram: null as string | null,

            schools: [] as CandidateSchool[],
            levels: [] as StudyLevel[],
            fields: [] as StudyField[],
            schoolStudyPrograms: new Map<string, StudyProgram[]>(),
        };
    },
    computed: {
        studyPrograms(): CandidateStudyProgram[] {
            const ret: CandidateStudyProgram[] = [];
            for (const p of this.programs) {
                const obj: CandidateStudyProgram = {
                    ...p,
                    programId: p.programId || '',
                    candidateId: this.builder.candidateId,
                    studyProgram: {
                        id: '',
                        name: '',
                        description: '',
                        schoolId: null,
                        customSchoolName: null,
                        levelId: '',
                        isVerified: false,
                        fields: [],

                        ...p.studyProgram,
                    },
                };

                if (!p.studyProgram && !!p.programId) {
                    const sp = this.getSchoolStudyProgram(p.programId);
                    if (!!sp) obj.studyProgram = sp;
                }

                if (!!obj.studyProgram.schoolId)
                    obj.studyProgram.school = this.schools.find(
                        s => s.id === obj.studyProgram.schoolId
                    );

                if (!!obj.studyProgram.levelId)
                    obj.studyProgram.level = this.levels.find(
                        l => l.id === obj.studyProgram.levelId
                    );

                ret.push(obj);
            }

            return ret.sort((a, b) => a.start.getTime() - b.start.getTime());
        },
    },
    methods: {
        async loadSchools() {
            const schools = await this.cspService.getSchools();
            this.schools = schools.sort((a, b) => a.name.localeCompare(b.name));
        },
        async loadLevels() {
            this.levels = await this.cspService.getStudyLevels();
        },
        async loadFields() {
            this.fields = await this.cspService.getStudyFields();
        },
        async loadSchoolStudyPrograms(id: string) {
            if (isNaN(Number(id))) return;
            if (this.schoolStudyPrograms.has(id)) return;

            const programs = await this.cspService.getSchoolStudyPrograms(Number(id));
            this.setSchoolStudyPrograms(id, programs || []);
        },

        setSchoolStudyPrograms(schoolId: string, programs: StudyProgram[]) {
            this.schoolStudyPrograms = new Map(this.schoolStudyPrograms.set(schoolId, programs));
        },
        getSchoolStudyProgram(programId: string, schoolId?: string | number) {
            if (typeof schoolId === 'undefined') {
                for (const [, programs] of this.schoolStudyPrograms) {
                    const program = programs.find(p => p.id === programId);
                    if (!!program) return program;
                }

                return null;
            } else {
                if (isNaN(Number(schoolId))) return null;

                const programs = this.schoolStudyPrograms.get(schoolId + '');
                if (!programs) return null;

                return programs.find(p => p.id === programId) || null;
            }
        },

        isUnsaved(program: CandidateStudyProgram) {
            return CandidateJobBuilder.isTempItem(program) && program.id !== this.editingProgram;
        },

        handleFormChange(form: CSPForm) {
            if (Number(form.school) < 0) return;
            this.loadSchoolStudyPrograms(form.school);
        },
        handleFormValidated(id: string, valid: boolean) {
            this.$nextTick(() => {
                if (this.editingProgram !== id) return;
                this.$emit('validated', valid);
            });
        },
        handleFormDelete(id: string) {
            this.editingProgram = null;
            this.programs = this.programs.filter(p => p.id !== id);
        },
        handleFormSave(form: CSPForm) {
            const currentPrograms = cloneDeep(this.programs);

            if (!!this.editingProgram) {
                const idx = currentPrograms.findIndex(p => p.id === this.editingProgram);
                this.editingProgram = null;
                if (idx < 0) return;

                const prog = currentPrograms[idx];
                prog.start = form.start;
                prog.end = form.end;

                if (!form.customSchool && !form.customProgram) {
                    delete prog.studyProgram;
                    prog.programId = form.program;

                    if (!!form.campus) prog.campusId = form.campus;
                } else {
                    prog.campusId = null;
                    if (form.customSchool)
                        prog.studyProgram = {
                            name: form.program,
                            levelId: form.level,
                            fields: [],
                            ...prog.studyProgram,
                            schoolId: null,
                            customSchoolName: form.school,
                            isVerified: false,
                        };
                    if (form.customProgram) {
                        prog.studyProgram = {
                            schoolId: prog.studyProgram ? prog.studyProgram.schoolId || null : null,
                            customSchoolName: prog.studyProgram
                                ? prog.studyProgram.customSchoolName || null
                                : null,
                            fields: [],
                            ...prog.studyProgram,
                            name: form.program,
                            levelId: form.level,
                            isVerified: false,
                        };

                        if (form.newProgram) {
                            prog.programId = undefined;
                            prog.studyProgram.id = undefined;
                            prog.studyProgram.isVerified = false;
                        }

                        const field = this.fields.find(f => f.id === form.field);
                        prog.studyProgram.fields = !!field ? [{ ...field }] : [];

                        if (!form.customSchool && !!form.school) {
                            prog.studyProgram.schoolId = Number(form.school);
                            prog.studyProgram.customSchoolName = null;
                        }
                    }
                }

                currentPrograms.splice(idx, 1, prog);
                this.programs = currentPrograms;
            }
        },

        handleAddStudyProgram() {
            const id = CandidateJobBuilder.generateId();
            this.programs.push({
                id,
                programId: '',
                start: new Date(),
                end: new Date(),
                campusId: undefined,
                studyProgram: {
                    id: '',
                    name: '',
                    schoolId: null,
                    customSchoolName: null,
                    levelId: '',
                    isVerified: false,
                    fields: [],
                },
            });
            this.$nextTick(() => {
                this.editingProgram = id;
            });
        },
    },
    mounted() {
        this.loadSchools();
        this.loadLevels();
        this.loadFields();

        for (const program of this.existingPrograms)
            if (program.studyProgram.schoolId)
                this.loadSchoolStudyPrograms(program.studyProgram.schoolId + '');

        this.$watch('programs', this.builder.watchCSPs());
    },
});
